import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Music,
  Info,
  BookOpen,
  Play,
  Pause,
  Volume2,
  ArrowLeft,
  Flag,
  Calendar,
  User,
  MapPin,
  AlertCircle,
  Loader2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const AUDIO_URL = `${BACKEND_URL}/api/static/marseillaise.ogg`;

const Hymne = () => {
  const navigate = useNavigate();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = new Audio(AUDIO_URL);
    audio.preload = "none";

    audio.addEventListener("ended", () => setIsPlaying(false));
    audio.addEventListener("error", () => {
      setAudioError(true);
      setIsPlaying(false);
      setIsLoading(false);
    });
    audio.addEventListener("canplay", () => setIsLoading(false));

    audioRef.current = audio;

    return () => {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    };
  }, []);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      try {
        setIsLoading(true);
        setAudioError(false);
        await audio.play();
        setIsPlaying(true);
        setIsLoading(false);
      } catch (err) {
        setIsPlaying(false);
        setIsLoading(false);
        setAudioError(true);
        toast.error("Impossible de lire l'audio. Vérifiez votre connexion.");
      }
    }
  }, [isPlaying]);

  const handleBack = () => {
    if (audioRef.current) audioRef.current.pause();
    navigate(-1);
  };

  const couplets = [
    {
      numero: "1er Couplet",
      paroles: `Allons enfants de la Patrie,
Le jour de gloire est arrivé !
Contre nous de la tyrannie,
L'étendard sanglant est levé, (bis)
Entendez-vous dans les campagnes
Mugir ces féroces soldats ?
Ils viennent jusque dans vos bras
Égorger vos fils, vos compagnes !`
    },
    {
      numero: "Refrain",
      paroles: `Aux armes, citoyens,
Formez vos bataillons,
Marchons, marchons !
Qu'un sang impur
Abreuve nos sillons !`,
      isRefrain: true
    },
    {
      numero: "2ème Couplet",
      paroles: `Que veut cette horde d'esclaves,
De traîtres, de rois conjurés ?
Pour qui ces ignobles entraves,
Ces fers dès longtemps préparés ? (bis)
Français, pour nous, ah ! quel outrage !
Quels transports il doit exciter !
C'est nous qu'on ose méditer
De rendre à l'antique esclavage !`
    },
    {
      numero: "3ème Couplet",
      paroles: `Quoi ! des cohortes étrangères
Feraient la loi dans nos foyers !
Quoi ! ces phalanges mercenaires
Terrasseraient nos fiers guerriers ! (bis)
Grand Dieu ! par des mains enchaînées
Nos fronts sous le joug se ploieraient
De vils despotes deviendraient
Les maîtres de nos destinées !`
    },
    {
      numero: "4ème Couplet",
      paroles: `Tremblez, tyrans et vous perfides
L'opprobre de tous les partis,
Tremblez ! vos projets parricides
Vont enfin recevoir leurs prix ! (bis)
Tout est soldat pour vous combattre,
S'ils tombent, nos jeunes héros,
La terre en produit de nouveaux,
Contre vous tout prêts à se battre !`
    },
    {
      numero: "5ème Couplet",
      paroles: `Français, en guerriers magnanimes,
Portez ou retenez vos coups !
Épargnez ces tristes victimes,
À regret s'armant contre nous. (bis)
Mais ces despotes sanguinaires,
Mais ces complices de Bouillé,
Tous ces tigres qui, sans pitié,
Déchirent le sein de leur mère !`
    },
    {
      numero: "6ème Couplet",
      paroles: `Amour sacré de la Patrie,
Conduis, soutiens nos bras vengeurs
Liberté, Liberté chérie,
Combats avec tes défenseurs ! (bis)
Sous nos drapeaux que la victoire
Accoure à tes mâles accents,
Que tes ennemis expirants
Voient ton triomphe et notre gloire !`
    },
    {
      numero: "7ème Couplet (Couplet des enfants)",
      paroles: `Nous entrerons dans la carrière
Quand nos aînés n'y seront plus,
Nous y trouverons leur poussière
Et la trace de leurs vertus (bis)
Bien moins jaloux de leur survivre
Que de partager leur cercueil,
Nous aurons le sublime orgueil
De les venger ou de les suivre !`
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="hymne-page">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <Button 
          variant="ghost" 
          onClick={handleBack}
          className="mb-6 text-slate-600"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </Button>

        {/* Hero Section */}
        <div className="bg-gradient-to-r from-blue-900 via-white to-red-600 p-1 rounded-2xl mb-8">
          <Card className="border-0">
            <CardContent className="p-8 text-center">
              <div className="h-20 w-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <Music className="h-10 w-10 text-blue-900" />
              </div>
              <h1 className="text-3xl md:text-4xl font-serif font-bold text-slate-900 mb-2">
                La Marseillaise
              </h1>
              <p className="text-lg text-slate-600 mb-6">
                Hymne national de la République française
              </p>
              
              {/* Audio Player */}
              <div className="flex items-center justify-center gap-4">
                <Button
                  onClick={togglePlay}
                  className="bg-blue-900 hover:bg-blue-800 gap-2"
                  size="lg"
                  disabled={isLoading}
                  data-testid="play-hymne-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Chargement...
                    </>
                  ) : isPlaying ? (
                    <>
                      <Pause className="h-5 w-5" />
                      Pause
                    </>
                  ) : (
                    <>
                      <Play className="h-5 w-5" />
                      Écouter l'hymne
                    </>
                  )}
                </Button>
                <div className="flex items-center gap-2 text-slate-500">
                  <Volume2 className="h-5 w-5" />
                  <span className="text-sm">Audio officiel</span>
                </div>
              </div>
              {audioError && (
                <div className="flex items-center justify-center gap-2 mt-4 text-red-600 text-sm" data-testid="audio-error-msg">
                  <AlertCircle className="h-4 w-4" />
                  <span>La lecture audio n'est pas disponible. Essayez un autre navigateur ou vérifiez votre connexion.</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="paroles" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="paroles" className="gap-2">
              <BookOpen className="h-4 w-4" />
              Paroles
            </TabsTrigger>
            <TabsTrigger value="histoire" className="gap-2">
              <Info className="h-4 w-4" />
              Histoire
            </TabsTrigger>
          </TabsList>

          {/* Paroles Tab */}
          <TabsContent value="paroles">
            <div className="space-y-6">
              {couplets.map((couplet, index) => (
                <Card 
                  key={index} 
                  className={couplet.isRefrain ? "border-2 border-blue-200 bg-blue-50" : ""}
                >
                  <CardHeader className="pb-2">
                    <CardTitle className={`text-lg font-serif ${couplet.isRefrain ? "text-blue-900" : "text-slate-700"}`}>
                      {couplet.isRefrain && <Flag className="h-5 w-5 inline mr-2 text-blue-900" />}
                      {couplet.numero}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className={`whitespace-pre-wrap font-serif text-lg leading-relaxed ${couplet.isRefrain ? "text-blue-900 font-semibold" : "text-slate-700"}`}>
                      {couplet.paroles}
                    </pre>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Histoire Tab */}
          <TabsContent value="histoire">
            <div className="space-y-6">
              {/* Informations clés */}
              <div className="grid md:grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 bg-amber-100 rounded-xl flex items-center justify-center shrink-0">
                        <User className="h-6 w-6 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Compositeur</p>
                        <p className="font-semibold text-slate-900">Claude Joseph Rouget de Lisle</p>
                        <p className="text-sm text-slate-600">Officier du génie</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 bg-blue-100 rounded-xl flex items-center justify-center shrink-0">
                        <Calendar className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Date de composition</p>
                        <p className="font-semibold text-slate-900">25-26 avril 1792</p>
                        <p className="text-sm text-slate-600">À Strasbourg</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 bg-green-100 rounded-xl flex items-center justify-center shrink-0">
                        <Flag className="h-6 w-6 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Hymne national depuis</p>
                        <p className="font-semibold text-slate-900">1879</p>
                        <p className="text-sm text-slate-600">Sous la IIIe République</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 bg-purple-100 rounded-xl flex items-center justify-center shrink-0">
                        <MapPin className="h-6 w-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Nom original</p>
                        <p className="font-semibold text-slate-900">Chant de guerre pour l'armée du Rhin</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Histoire détaillée */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-serif">Histoire de La Marseillaise</CardTitle>
                </CardHeader>
                <CardContent className="prose prose-slate max-w-none">
                  <p className="text-slate-600 leading-relaxed mb-4">
                    <strong>La Marseillaise</strong> a été composée dans la nuit du 25 au 26 avril 1792 par 
                    <strong> Claude Joseph Rouget de Lisle</strong>, capitaine du génie en garnison à Strasbourg, 
                    à la suite de la déclaration de guerre de la France à l'Autriche.
                  </p>

                  <p className="text-slate-600 leading-relaxed mb-4">
                    Initialement intitulée <em>« Chant de guerre pour l'armée du Rhin »</em>, elle fut rebaptisée 
                    <em>« La Marseillaise »</em> car elle fut popularisée par les fédérés marseillais qui 
                    la chantaient lors de leur entrée triomphale à Paris le 30 juillet 1792.
                  </p>

                  <h4 className="font-semibold text-slate-900 mt-6 mb-2">Chronologie :</h4>
                  <ul className="text-slate-600 space-y-2">
                    <li><strong>1792</strong> : Composition et adoption comme chant révolutionnaire</li>
                    <li><strong>14 juillet 1795</strong> : Déclarée hymne national par la Convention</li>
                    <li><strong>1804-1815</strong> : Interdite sous l'Empire et la Restauration</li>
                    <li><strong>1879</strong> : Redevient officiellement l'hymne national</li>
                    <li><strong>1958</strong> : Inscrite dans la Constitution de la Ve République (article 2)</li>
                  </ul>

                  <div className="bg-blue-50 p-4 rounded-lg mt-6">
                    <p className="text-blue-900 font-medium">
                      📜 Article 2 de la Constitution : « L'hymne national est La Marseillaise. »
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* À retenir pour l'examen */}
              <Card className="bg-amber-50 border-amber-200">
                <CardHeader>
                  <CardTitle className="font-serif flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-amber-600" />
                    À retenir pour l'examen civique
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="h-6 w-6 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-medium shrink-0">1</span>
                      <span className="text-slate-700"><strong>Compositeur :</strong> Claude Joseph Rouget de Lisle</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="h-6 w-6 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-medium shrink-0">2</span>
                      <span className="text-slate-700"><strong>Année de composition :</strong> 1792 (à Strasbourg)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="h-6 w-6 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-medium shrink-0">3</span>
                      <span className="text-slate-700"><strong>Hymne national depuis :</strong> 1879</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="h-6 w-6 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-medium shrink-0">4</span>
                      <span className="text-slate-700"><strong>Nom d'origine :</strong> Chant de guerre pour l'armée du Rhin</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="h-6 w-6 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-medium shrink-0">5</span>
                      <span className="text-slate-700"><strong>Constitution :</strong> Inscrite à l'article 2</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Action Button */}
        <div className="mt-8 flex justify-center">
          <Button
            onClick={() => navigate("/quiz/symboles")}
            className="bg-blue-900 hover:bg-blue-800"
            data-testid="quiz-symboles-btn"
          >
            <Play className="h-4 w-4 mr-2" />
            Quiz sur les symboles nationaux
          </Button>
        </div>
      </main>
    </div>
  );
};

export default Hymne;
